import { useMemo } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

/**
 * Horizontal target gauge. Shows the current margin position along a 0..max% bar
 * with the target line drawn vertically. Color adapts to status.
 *
 * Props:
 *  - data: profitability dict from GET /api/work-tracking/orders-profitability
 *  - size: 'sm' | 'md' | 'lg'
 */
export const ProfitabilityGauge = ({ data, size = "md" }) => {
  const { currentPct, targetPct, scaleMax, status, info } = useMemo(() => {
    if (!data) return { currentPct: 0, targetPct: 0, scaleMax: 100, status: "no_data" };
    const tgt = Math.max(0, Math.min(100, data.target_margin_percent || 0));
    const cur = Math.max(-100, Math.min(200, data.net_margin_percent || 0));
    // Scale: stretch to at least target+20 for headroom, cap at 100
    const max = Math.max(100, tgt + 20);
    return { currentPct: cur, targetPct: tgt, scaleMax: max, status: data.status, info: data };
  }, [data]);

  if (!data || data.status === "no_data") {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-center text-xs text-muted-foreground" data-testid="profit-gauge-empty">
        Aucun coût saisi pour cette commande (matière + main-d'œuvre) — saisissez des matériaux ou des heures pour voir la rentabilité.
      </div>
    );
  }

  const cfg = {
    above_target: { color: "#059669", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", Icon: CheckCircle2, label: "Chantier rentable" },
    near_target:  { color: "#D97706", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", Icon: AlertTriangle, label: "Proche de la cible" },
    below_target: { color: "#DC2626", bg: "bg-red-50", border: "border-red-200", text: "text-red-800", Icon: AlertTriangle, label: "Sous la cible" },
    negative:     { color: "#991B1B", bg: "bg-red-100", border: "border-red-300", text: "text-red-900", Icon: XCircle, label: "Chantier déficitaire" },
  }[status] || { color: "#475569", bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-800", Icon: AlertTriangle, label: "—" };

  const { Icon } = cfg;

  // Convert percentages into bar positions (0..100 of the visible bar)
  // currentPct can be negative → clamp at 0 visually but keep sign in the label
  const clamped = Math.max(0, Math.min(scaleMax, currentPct));
  const curBarPct = (clamped / scaleMax) * 100;
  const targetBarPct = (targetPct / scaleMax) * 100;
  const heightClass = size === "sm" ? "h-2" : size === "lg" ? "h-6" : "h-4";

  return (
    <div className={`rounded-lg border ${cfg.border} ${cfg.bg} p-4`} data-testid={`profit-gauge-${status}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`flex items-center gap-2 font-heading font-bold ${cfg.text}`}>
          <Icon className="h-5 w-5" />
          <span>{cfg.label}</span>
        </div>
        <div className="flex items-baseline gap-2 font-mono">
          <span className="text-2xl font-bold" style={{ color: cfg.color }} data-testid="profit-gauge-current">
            {currentPct}%
          </span>
          <span className="text-xs text-muted-foreground">
            / cible {targetPct}%
          </span>
        </div>
      </div>

      <div className={`relative w-full ${heightClass} bg-white/70 rounded overflow-hidden border border-black/5`}>
        {/* Progress bar */}
        <div
          className="h-full transition-all"
          style={{ width: `${curBarPct}%`, background: cfg.color, opacity: 0.85 }}
        />
        {/* Target line */}
        <div
          className="absolute top-0 bottom-0 border-l-2 border-dashed border-slate-700"
          style={{ left: `${targetBarPct}%` }}
          title={`Cible ${targetPct}%`}
        />
      </div>

      {info && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
          <div>
            <div className="text-muted-foreground">CA HT</div>
            <div className="font-semibold">{info.amount_ht?.toLocaleString("fr-FR")} €</div>
          </div>
          <div>
            <div className="text-muted-foreground flex items-center gap-1">
              Matière
              {info.material_source === "manual" && (
                <span className="text-[10px] px-1 py-0 rounded bg-indigo-100 text-indigo-700 uppercase" title="Coût saisi manuellement">manuel</span>
              )}
            </div>
            <div className="font-semibold text-red-700">{info.material_cost?.toLocaleString("fr-FR")} €</div>
          </div>
          <div>
            <div className="text-muted-foreground">Main-d'œuvre</div>
            <div className="font-semibold text-red-700">{info.labor_cost?.toLocaleString("fr-FR")} €</div>
          </div>
          <div>
            <div className="text-muted-foreground">Marge nette</div>
            <div className={`font-semibold ${info.net_margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {info.net_margin?.toLocaleString("fr-FR")} €
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitabilityGauge;
